import {
  Fragment,
  lazy,
  Suspense,
  useEffect,
  useState,
  type CSSProperties,
} from 'react';
import { Boxes, Check, Cloud, Smartphone, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Container } from '@/components/ui/container';
import { Card } from '@/components/ui/card';
import { Reveal } from '@/components/ui/reveal';
import { Section, SectionHeading } from '@/components/ui/section';
import { SecurityCardTexture } from '@/components/three/SecurityCardTexture';
import { useNearViewport } from '@/components/three/useNearViewport';

const SecurityLayerScene = lazy(() =>
  import('@/components/three/SecurityLayerScene').then((module) => ({
    default: module.SecurityLayerScene,
  }))
);

type Layer = {
  index: string;
  where: string;
  name: string;
  description: string;
  icon: LucideIcon;
};

type LayerState = 'waiting' | 'active' | 'verified' | 'complete';

/** The three independent layers every transaction must clear, device → chain. */
const LAYERS: Layer[] = [
  {
    index: '1',
    where: 'On device',
    name: 'Mobile & SDK Signers',
    description:
      'Transactions are validated locally before being signed by the mobile app or SDK.',
    icon: Smartphone,
  },
  {
    index: '2',
    where: 'In the cloud',
    name: 'Guardian Co-Signer',
    description:
      'The network-isolated Guardian validates your transactions against your policies.',
    icon: Cloud,
  },
  {
    index: '3',
    where: 'On the blockchain',
    name: 'Onchain Smart Contracts',
    description:
      'Smart contracts validate your transactions against your policies onchain.',
    icon: Boxes,
  },
];

/* -------------------------------------------------------------------------- */
/*  Transaction-flow animation                                                */
/* -------------------------------------------------------------------------- */

/** Hold long enough to understand each validation before advancing. */
const ACTIVE_MS = 1500;
/** Fade between waiting, checking, and verified states. */
const RAMP_MS = 400;
const STEP_MS = ACTIVE_MS + RAMP_MS;
/** A fourth beat holds every layer in its passed state before restarting. */
const FINAL_STEP = LAYERS.length;

const FLOW_TRANSITION = 'flow-transition';

/** Lucide-style arrow with a longer shaft for the inter-card flow. */
function FlowArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      overflow="visible"
      className={className}
    >
      <path d="M0 12h19" />
      <path d="m13.5 6.5 5.5 5.5-5.5 5.5" />
    </svg>
  );
}

function getLayerState(layerIndex: number, step: number): LayerState {
  if (step === FINAL_STEP) return 'complete';
  if (step < 0 || layerIndex > step) return 'waiting';
  if (layerIndex === step) return 'active';
  return 'verified';
}

/** Connectors 1 and 2 link the three validation layers. */
function FlowConnector({ step, edge }: { step: number; edge: number }) {
  const complete = step === FINAL_STEP;
  const active = !complete && step >= 0 && (edge === step || edge === step + 1);
  const leftLayerIndex = edge - 1;
  const leftLayerActive = !complete && step === leftLayerIndex;
  const leftLayerVerified = complete || (step >= 0 && step > leftLayerIndex);
  const leftOfActive = active && edge === step;
  const rightOfActive = active && edge === step + 1;

  return (
    <div className="flex shrink-0 items-center justify-center py-1.5 lg:w-8 lg:py-0">
      <span
        className={cn(
          'relative grid size-8 rotate-90 place-items-center border-0 bg-transparent shadow-none lg:rotate-0',
          FLOW_TRANSITION,
          leftOfActive
            ? 'translate-y-1 lg:translate-x-1 lg:translate-y-0'
            : rightOfActive
              ? '-translate-y-1 lg:-translate-x-1 lg:translate-y-0'
              : '',
          leftLayerActive
            ? 'text-blue-300 opacity-70'
            : leftLayerVerified
              ? 'text-green-300 opacity-70'
              : 'text-zinc-300 opacity-70'
        )}
      >
        <FlowArrowIcon
          className={cn(
            'size-6',
            FLOW_TRANSITION,
            leftOfActive
              ? 'scale-110 lg:translate-x-0.35'
              : rightOfActive
                ? 'scale-110 lg:-translate-x-0.35'
                : 'scale-100'
          )}
        />
      </span>
    </div>
  );
}

function LayerStateMark({
  state,
  className,
}: {
  state: LayerState;
  className?: string;
}) {
  const passed = state === 'verified' || state === 'complete';

  return (
    <div
      className={cn(
        'grid size-7 shrink-0 place-items-center',
        passed && 'rounded-full bg-emerald-300 shadow-sm shadow-black/20',
        className
      )}
    >
      {state === 'active' ? (
        <span className="sec-status-spinner size-7 rounded-full border-[3px] border-blue-200/20 border-t-sky-300" />
      ) : passed ? (
        <Check
          className="sec-status-check size-[1.05rem] text-emerald-950"
          strokeWidth={3.8}
        />
      ) : null}
    </div>
  );
}

/** A single validation stage with a textured surface and 3D scene. */
function LayerCard({
  layer,
  layerIndex,
  state,
  decorationsReady,
}: {
  layer: Layer;
  layerIndex: number;
  state: LayerState;
  decorationsReady: boolean;
}) {
  const { where, name, description, icon: Icon } = layer;
  const active = state === 'active';
  const verified = state === 'verified' || state === 'complete';

  return (
    <Card
      data-state={state}
      className={cn(
        'security-layer-card relative isolate flex flex-col overflow-hidden rounded-xl border-none px-5 py-5 text-white shadow-[0_18px_46px_-30px_rgba(0,0,0,0.8)] lg:h-[31rem] lg:p-6 xl:h-[29rem]',
        FLOW_TRANSITION,
        'min-h-[19rem] sm:min-h-[20rem] lg:min-h-0',
        active
          ? 'scale-[1.012] shadow-[0_24px_52px_-30px_rgba(0,0,0,0.82)]'
          : verified
            ? 'sec-layer-passed shadow-[0_22px_48px_-30px_rgba(0,0,0,0.78)]'
            : 'scale-[0.99]'
      )}
    >
      <div
        aria-hidden
        className="security-card-texture pointer-events-none absolute inset-0 -z-10 rounded-[inherit]"
      >
        <SecurityCardTexture state={state} className="opacity-95" />
      </div>
      <div
        aria-hidden
        className="sec-layer-flash pointer-events-none absolute inset-0 z-40 rounded-[inherit]"
      />

      <LayerStateMark
        state={state}
        className="absolute right-5 top-4 z-50 size-8 lg:right-6 lg:top-5"
      />

      <div
        className={cn(
          'inline-flex w-fit items-center gap-2 rounded-full px-2.5 py-1.5 ring-1 ring-inset',
          FLOW_TRANSITION,
          active
            ? 'bg-blue-200/[0.035] text-blue-50/60 ring-blue-100/[0.04]'
            : verified
              ? 'bg-green-200/[0.035] text-green-50/60 ring-green-100/[0.04]'
              : 'bg-zinc-200/[0.035] text-zinc-50/60 ring-white/[0.03]'
        )}
      >
        <Icon className="size-3.5 shrink-0 opacity-80" strokeWidth={2} />
        <span className="text-[0.62rem] font-semibold uppercase tracking-[0.09em]">
          {where}
        </span>
      </div>

      <div className="mt-4">
        <h3
          className={cn(
            'mt-0 text-xl font-medium leading-tight lg:text-2xl',
            FLOW_TRANSITION,
            active || verified ? 'text-white' : 'text-zinc-300'
          )}
        >
          {name}
        </h3>
        <p
          className={cn(
            'mt-2 text-xs leading-relaxed lg:text-sm',
            FLOW_TRANSITION,
            active || verified ? 'text-zinc-300' : 'text-zinc-400'
          )}
        >
          {description}
        </p>
      </div>

      <div
        aria-hidden="true"
        className="relative mt-2.5 -mb-5 -ml-5 min-h-[8.5rem] w-[calc(100%+2.5rem)] flex-1 sm:min-h-[11rem] lg:-mb-6 lg:-ml-6 lg:min-h-[13.5rem] lg:w-[calc(100%+3rem)]"
      >
        {decorationsReady && (
          <Suspense fallback={null}>
            <SecurityLayerScene
              layerIndex={layerIndex}
              state={state}
              className="absolute inset-0"
            />
          </Suspense>
        )}
      </div>
    </Card>
  );
}

export function SecurityLayers() {
  const [reducedMotion, setReducedMotion] = useState(false);

  // Reduced motion renders the finished pipeline; otherwise it rests at step 0
  // (first layer active, the rest waiting) until the flow is scrolled into view.
  const [step, setStep] = useState(() => (reducedMotion ? FINAL_STEP : 0));

  // Only begin advancing once the pipeline first enters the viewport, so the
  // user always catches the animation at step 0 rather than mid-cycle.
  const { ref: pipelineRef, ready: decorationsReady } =
    useNearViewport<HTMLDivElement>();
  const [inView, setInView] = useState(reducedMotion);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      setReducedMotion(query.matches);
      // Case: reduced motion shows the completed pipeline without waiting or animating.
      if (query.matches) {
        setStep(FINAL_STEP);
        setInView(true);
      }
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const node = pipelineRef.current;
    if (!node || inView || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(node);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, pipelineRef]);

  useEffect(() => {
    if (!inView || reducedMotion) return;
    const id = window.setInterval(
      () => setStep((current) => (current + 1) % (LAYERS.length + 1)),
      STEP_MS
    );
    return () => window.clearInterval(id);
  }, [inView, reducedMotion]);

  return (
    <Section
      id="security"
      className="overflow-hidden border-t border-white/10 bg-zinc-900/[98]"
    >
      <div aria-hidden className="security-section-grid absolute inset-0" />

      <Container className="relative">
        <SectionHeading
          align="left"
          className="items-center text-center sm:items-start sm:text-left"
          eyebrow="Multi-layer security"
          eyebrowClassName="gap-2.5 text-zinc-400 [&>span]:bg-sky-400"
          title={
            <span className="font-medium leading-relaxed text-white lg:leading-[55px]">
              Protect your assets with{' '}
              <span className="text-white">multiple layers of security</span>
            </span>
          }
          description={
            <>
              Every transaction passes three independent security layers.
              <br /> No single compromise can move your assets.
            </>
          }
          descriptionClassName="!text-zinc-300"
        />

        <div
          ref={pipelineRef}
          data-running={inView}
          className="security-layer-pipeline relative isolate mt-10 lg:mt-14"
          style={
            {
              '--flow-ramp': `${RAMP_MS}ms`,
              '--stage-duration': `${STEP_MS}ms`,
            } as CSSProperties
          }
        >
          <div className="flex flex-col items-stretch gap-1 lg:flex-row lg:gap-2">
            {LAYERS.map((layer, index) => (
              <Fragment key={layer.index}>
                <Reveal className="min-w-0 lg:flex-1" delay={index * 100}>
                  <LayerCard
                    layer={layer}
                    layerIndex={index}
                    state={getLayerState(index, step)}
                    decorationsReady={decorationsReady}
                  />
                </Reveal>
                {index < LAYERS.length - 1 && (
                  <FlowConnector step={step} edge={index + 1} />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
